import * as React from "react";

export const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving a version?";

/**
 * Warns before losing unsaved editor changes: `beforeunload` for reloads/closes and a capture
 * phase click guard for in-app links (the app uses `BrowserRouter`, which has no `useBlocker`).
 */
export const useUnsavedChangesGuard = (dirty: boolean): void => {
  React.useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy browsers read the assigned string; modern ones show their own copy.
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
    };
    const onClickCapture = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank" || anchor.hasAttribute("download")) {
        return;
      }
      if (anchor.origin !== window.location.origin) return;
      if (anchor.pathname === window.location.pathname && anchor.search === window.location.search) return;
      if (!window.confirm(UNSAVED_CHANGES_MESSAGE)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [dirty]);
};
